import React, { useState, useEffect } from 'react';
import { Card, Col, Form, Input, Row } from 'antd';
import styled from '@emotion/styled';

interface AnswerOption {
  id?: string;
  title: string;
  subTitle?: string;
  imageLink?: string;
}

interface Props {
  t: (key: string, opts?: any) => string;
  answerOptions: AnswerOption[];
  onOptionChange: (index: number, field: keyof AnswerOption, value: string) => void;
  readOnly?: boolean;
}

const SingleColumnAnswers: React.FC<Props> = ({ t, answerOptions, onOptionChange, readOnly = false }) => {
  const [charCounts, setCharCounts] = useState<{ [key: string]: number }>({});

  const maxLength = 42;

  const handleCharCountChange = (optionId: string, value: string) => {
    setCharCounts(prev => ({
      ...prev,
      [optionId]: value.length,
    }));
  };

  const handleInputChange = (index: number, field: keyof AnswerOption, value: string) => {
    const truncatedValue = value.slice(0, maxLength);
    onOptionChange(index, field, truncatedValue);
    handleCharCountChange(answerOptions[index].id || `option-${index}`, truncatedValue);
  };

  useEffect(() => {
    const initialCounts: { [key: string]: number } = {};
    answerOptions.forEach((option, index) => {
      const optionId = option.id || `option-${index}`;
      initialCounts[optionId] = option.title.length;
    });
    setCharCounts(initialCounts);
  }, [answerOptions]);

  return (
    <StyledAnswerSection>
      <Row gutter={[16, 8]}>
        {answerOptions.map((option, index) => {
          const optionId = option.id || `option-${index}`;
          const currentCount = charCounts[optionId] || 0;

          return (
            <Col span={12} key={option.id}>
              <StyledAnswerCardCompact>
                <Form.Item label={`${t('questionForm.labels.answerTitle')} ${index + 1}`} required={index < 2}>
                  <div className="position-relative">
                    <Input
                      value={option.title}
                      onChange={e => handleInputChange(index, 'title', e.target.value)}
                      placeholder={t('questionForm.placeholders.title')}
                      maxLength={maxLength}
                      disabled={readOnly}
                    />
                    <div className="count-item">{`${currentCount}/${maxLength}`}</div>
                  </div>
                </Form.Item>
              </StyledAnswerCardCompact>
            </Col>
          );
        })}
      </Row>
    </StyledAnswerSection>
  );
};

const StyledAnswerSection = styled.div`
  margin-bottom: 24px;

  .position-relative {
    position: relative;
  }

  .count-item {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
    color: #cbcbcb;
    pointer-events: none;
  }

  input {
    padding-right: 40px;
  }
`;

const StyledAnswerCardCompact = styled(Card)`
  border: none;
  .ant-card-body {
    padding: 0 12px;
  }
`;

export default SingleColumnAnswers;
